// Lib
import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Components
import Slider from '../../../../Slider';
import ShippingCard from '../../ShippingCard';
import PlusButton from '../../../../OrderHistory/Buttons/PlusButton';
import AddressCardHeader from '../../../../AddressBook/Card/AddressCardHeader/AddressCardHeader';

// Styles
import { useStyles } from './ShippingAddressesStyles';

const ShippingAddresses = ({
  shippingAddresses,
  isOrderPlaced = false,
  goToAddresses = () => {},
  onDeleteAddress = () => {},
  isOrderDetailsPage = false,
  isRecurringOrderPage = false,
  isReviewOrderPage = false,
  trackingLink = '',
  orderId,
  className,
}) => {
  const navigate = useNavigate();
  const isDistroOrder = useMemo(
    () => shippingAddresses?.length > 1,
    [shippingAddresses],
  );
  const { classes, cx } = useStyles({
    isDistroOrder,
    isReviewOrderPage,
    isOrderPlaced,
  });

  const title = useMemo(() => {
    const title = `Shipping Addresses (${shippingAddresses?.length})`;
    if (isDistroOrder && !isRecurringOrderPage) {
      return title;
    }
    if (isDistroOrder && isRecurringOrderPage) {
      return (
        <>
          {title}
          <PlusButton
            onClick={() =>
              navigate(`/order-history/recurring/${orderId}/address-book`)
            }
            className={classes.plusButton}
          />
        </>
      );
    }
  }, [
    navigate,
    orderId,
    isDistroOrder,
    shippingAddresses,
    isRecurringOrderPage,
    classes,
  ]);

  const renderFullAddress = () => {
    return (
      shippingAddresses?.[0]?.full_address && (
        <div
          className={classes.fullAddress}
          data-testid="shipping-address-text">
          {shippingAddresses?.[0]?.full_address}
        </div>
      )
    );
  };
  return (
    <div className={classes.addressSection}>
      {!isDistroOrder && !(isOrderDetailsPage || isRecurringOrderPage) && (
        <Typography
          className={cx(classes.shippingTitle, className)}
          variant="h3"
          data-testid="shipping-address-header">
          Shipping Address
        </Typography>
      )}
      {!isDistroOrder && (isOrderDetailsPage || isRecurringOrderPage) && (
        <Typography
          className={classes.shippingTitle}
          variant="h3"
          data-testid="shipping-address-header">
          {`Shipping Addresses (${shippingAddresses.length})`}
          {isRecurringOrderPage && (
            <PlusButton
              onClick={() =>
                navigate(`/order-history/recurring/${orderId}/address-book`)
              }
              className={classes.plusButton}
            />
          )}
        </Typography>
      )}
      {isDistroOrder ? (
        <Slider title={title}>
          {shippingAddresses.map((address) => {
            const fullAddress = address?.fullAddress || address?.full_address;
            const addressId =
              address?.id ||
              address?.address?.id ||
              address?.address_fields?.id;

            return (
              <ShippingCard
                dataTestIdForCard={`shipping-address-${addressId}`}
                trackingLink={trackingLink}
                multipleMode
                onDeleteAddress={onDeleteAddress(addressId)}
                isOrderPlaced={isOrderPlaced || isOrderDetailsPage}
                className={classes.shippingCard}
                key={addressId}
                onChange={goToAddresses}
                title={<AddressCardHeader address={address} />}
                dataTestId="shipping-address-title">
                {fullAddress && (
                  <div
                    className={classes.fullAddress}
                    data-testid="shipping-address-text">
                    {fullAddress}
                  </div>
                )}
              </ShippingCard>
            );
          })}
        </Slider>
      ) : (
        <ShippingCard
          withControls={!isRecurringOrderPage}
          trackingLink={trackingLink}
          isOrderPlaced={isOrderPlaced || isOrderDetailsPage}
          className={classes.singleShippingCard}
          onChange={goToAddresses}
          title={<AddressCardHeader address={shippingAddresses?.[0]} />}
          dataTestId="shipping-address-title">
          {renderFullAddress()}
        </ShippingCard>
      )}
    </div>
  );
};

export default ShippingAddresses;
